import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getCategoryLabel } from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">
              文章未找到
            </h1>
            <Link
              to="/"
              className="text-primary-500 hover:text-primary-600 font-medium"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const contentHtml = article.content
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (trimmed.startsWith('## ')) {
        return `<h2 class="text-xl font-heading font-bold text-foreground-900 mt-8 mb-4">${trimmed.replace('## ', '')}</h2>`;
      }
      if (trimmed.startsWith('### ')) {
        return `<h3 class="text-lg font-heading font-bold text-foreground-800 mt-6 mb-3">${trimmed.replace('### ', '')}</h3>`;
      }
      if (trimmed.startsWith('- **')) {
        const match = trimmed.match(/- \*\*(.+?)\*\*[：:]?\s*(.*)/);
        if (match) {
          return `<li class="ml-4 mb-1 text-foreground-700"><strong>${match[1]}</strong>：${match[2]}</li>`;
        }
      }
      if (trimmed.startsWith('- ')) {
        return `<li class="ml-4 mb-1 text-foreground-700">${trimmed.replace('- ', '')}</li>`;
      }
      if (trimmed.startsWith('**示例：**')) {
        return `<p class="text-sm bg-background-100 rounded-lg px-4 py-3 my-3 text-foreground-700 font-mono">${trimmed.replace('**示例：**', '示例：')}</p>`;
      }
      if (trimmed.startsWith('**Q:')) {
        return `<p class="font-medium text-foreground-900 mt-4 mb-1">${trimmed.replace(/\*\*/g, '')}</p>`;
      }
      if (trimmed.startsWith('**A:')) {
        return `<p class="text-foreground-600 mb-4">${trimmed.replace(/\*\*/g, '')}</p>`;
      }
      if (trimmed.startsWith('|')) {
        return `<p class="text-sm text-foreground-600 font-mono bg-background-100 rounded px-3 py-1 my-1 overflow-x-auto">${trimmed}</p>`;
      }
      if (trimmed === '---') {
        return '<hr class="my-8 border-background-200" />';
      }
      if (trimmed === '') {
        return '<br />';
      }
      return `<p class="text-foreground-700 leading-relaxed mb-3">${trimmed}</p>`;
    })
    .join('');

  const tableOfContents = article.content
    .split('\n')
    .filter((line) => line.trim().startsWith('## ') || line.trim().startsWith('### '))
    .map((line) => {
      const isH2 = line.trim().startsWith('## ');
      return {
        text: line.trim().replace(/^#+\s*/, ''),
        level: isH2 ? 2 : 3,
      };
    });

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              {
                label: getCategoryLabel(article.category),
                href: `/category/${article.category}`,
              },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                {getCategoryLabel(article.category)}
              </span>
              <span className="text-xs text-foreground-500">{article.date}</span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight mb-3">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">
              {article.description}
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 rounded-md text-xs bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="flex gap-8">
            <aside className="hidden lg:block w-56 flex-shrink-0">
              <div className="sticky top-24">
                <h4 className="text-sm font-heading font-bold text-foreground-900 mb-3">
                  目录导航
                </h4>
                <nav className="space-y-1">
                  {tableOfContents.map((item, idx) => (
                    <a
                      key={idx}
                      href={`#toc-${idx}`}
                      className={`block text-xs transition-colors py-1 ${
                        item.level === 2
                          ? 'text-foreground-700 hover:text-primary-500 font-medium'
                          : 'text-foreground-500 hover:text-primary-500 pl-3'
                      }`}
                    >
                      {item.text}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover rounded-lg mb-8"
              />

              <div
                className="prose-content text-sm leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: tableOfContents.reduce(
                    (html, item, idx) =>
                      html.replace(
                        item.text,
                        `<span id="toc-${idx}">${item.text}</span>`
                      ),
                    contentHtml
                  ),
                }}
              />
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-14">
            <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">
              相关推荐
            </h3>
            <div className="space-y-3">
              {relatedArticles.map((ra) => (
                <ArticleCard key={ra.slug} article={ra} variant="compact" />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />

      {article && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.description,
              datePublished: article.date,
              author: {
                '@type': 'Organization',
                name: 'P站视频全攻略',
              },
              publisher: {
                '@type': 'Organization',
                name: 'P站视频全攻略',
              },
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://pstation-guide.readdy.ai/article/${article.slug}`,
              },
            }),
          }}
        />
      )}
    </>
  );
}